import { liveRenderAtom } from './zeroTTSInstaller';
import { resolveAtomAtRuntime } from './zeroTTSSelector';
import { atomBucketName, readManifest } from './zeroTTSManifest';
import type {
  AtomManifest,
  TTSProfile,
  VoiceIdentity,
} from './zeroTTSProfile';

export interface PlaybackToken {
  stop: () => void;
  promise: Promise<void>;
  atomText: string;
  durationMs: number;
}

function noopToken(): PlaybackToken {
  return {
    stop: () => undefined,
    promise: Promise.resolve(),
    atomText: '',
    durationMs: 0,
  };
}

export class ZeroTTSPlayer {
  private ctx: AudioContext;
  private outputNode: AudioNode;
  private cache: Cache | null = null;
  private manifest: AtomManifest | null = null;
  private callCounter = 0;
  private activeSource: AudioBufferSourceNode | null = null;
  private activeResolve: (() => void) | null = null;
  private disposed = false;
  // True iff we created this AudioContext ourselves and therefore own
  // its lifecycle. When the parent passes one in (e.g. the shared
  // useAudioPlayer speech-bus ctx), dispose() must not close it.
  private ownsCtx: boolean;

  constructor(
    private profile: TTSProfile,
    /**
     * Stage 26.6: full voice identity. Single-mode installs pass
     * `{ kind: 'single', voiceId }`; blend-mode installs pass the pinned
     * VoiceSpec so cache-miss live-synth fallbacks stay on the exact voice
     * the user chose.
     */
    private identity: VoiceIdentity,
    private fingerprint: string,
    ctx?: AudioContext,
    /**
     * Stage 43.1: optional output graph node. When provided, BufferSources
     * connect to this node (e.g. the DOT speech-bus GainNode) instead of
     * the destination directly, so the analyser tap sees ZeroTTS atoms.
     * Defaults to `ctx.destination` for back-compat.
     */
    outputNode?: AudioNode,
  ) {
    if (ctx) {
      this.ctx = ctx;
      this.ownsCtx = false;
    } else {
      this.ctx = new AudioContext();
      this.ownsCtx = true;
    }
    this.outputNode = outputNode ?? this.ctx.destination;
  }

  getIdentity(): VoiceIdentity {
    return this.identity;
  }

  getFingerprint(): string {
    return this.fingerprint;
  }

  getPersonaSeed(): string {
    return this.profile.persona_seed;
  }

  async ready(): Promise<void> {
    if (this.disposed) throw new Error('PLAYER_DISPOSED');
    if (typeof caches === 'undefined') {
      throw new Error('CACHE_STORAGE_UNAVAILABLE');
    }
    this.cache = await caches.open(
      atomBucketName(this.profile.persona_seed, this.fingerprint),
    );
    this.manifest = await readManifest(this.profile.persona_seed);
    if (!this.manifest) throw new Error('PERSONA_NOT_INSTALLED');
    if (
      this.manifest.voice_fingerprint &&
      this.manifest.voice_fingerprint !== this.fingerprint
    ) {
      throw new Error('VOICE_FINGERPRINT_MISMATCH');
    }
  }

  stopActive(): void {
    if (this.activeSource) {
      try {
        this.activeSource.stop();
      } catch {
        /* already stopped */
      }
      try {
        this.activeSource.disconnect();
      } catch {
        /* noop */
      }
      this.activeSource = null;
    }
    if (this.activeResolve) {
      const r = this.activeResolve;
      this.activeResolve = null;
      r();
    }
  }

  async speak(
    contextName: string,
    payload: Record<string, string> = {},
  ): Promise<PlaybackToken> {
    if (this.disposed) return noopToken();
    if (!this.cache || !this.manifest) throw new Error('PLAYER_NOT_READY');

    if (this.ctx.state === 'suspended') {
      await this.ctx.resume().catch(() => undefined);
    }

    const callIdx = this.callCounter++;
    const { atomText, cacheKey, keyHashHex } = resolveAtomAtRuntime(
      this.profile,
      contextName,
      callIdx,
      payload,
    );

    const entry = this.manifest.atoms[keyHashHex];
    let arrBuf: ArrayBuffer | null = null;

    if (entry?.status === 'rendered') {
      const res = await this.cache.match(cacheKey);
      if (res) arrBuf = await res.arrayBuffer();
    }

    if (!arrBuf) {
      // eslint-disable-next-line no-console
      console.info(`[zerotts] cache miss — live-synth "${atomText}"`);
      try {
        const { wav } = await liveRenderAtom(this.identity, atomText);
        arrBuf = await wav.arrayBuffer();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[zerotts] live-synth fallback failed:`, err);
        return {
          ...noopToken(),
          atomText,
        };
      }
    }

    this.stopActive();

    let audioBuf: AudioBuffer;
    try {
      audioBuf = await this.ctx.decodeAudioData(arrBuf);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[zerotts] decodeAudioData failed for "${atomText}":`, err);
      return { ...noopToken(), atomText };
    }

    const src = this.ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(this.outputNode);

    let resolvePromise: () => void = () => undefined;
    const promise = new Promise<void>((resolve) => {
      resolvePromise = resolve;
    });
    this.activeResolve = resolvePromise;

    src.onended = () => {
      if (this.activeSource === src) {
        this.activeSource = null;
      }
      if (this.activeResolve === resolvePromise) {
        this.activeResolve = null;
        resolvePromise();
      }
    };

    this.activeSource = src;
    try {
      src.start();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[zerotts] source.start() failed:`, err);
      this.stopActive();
      return { ...noopToken(), atomText };
    }

    return {
      stop: () => this.stopActive(),
      promise,
      atomText,
      durationMs: Math.round(audioBuf.duration * 1000),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopActive();
    if (this.ownsCtx) {
      this.ctx.close().catch(() => undefined);
    }
  }
}
